// editions.ts — CORE-1660
//
// Действующие редакции юридических документов: что именно клиент называет
// серверу, когда человек с чем-то соглашается.
//
// Источник — `legal-editions/manifest.json` (собирается
// `scripts/legal-editions/build.js` из тех же исходников, что видит человек).
// В сборку попадает только манифест — версии и хеши; сами тексты редакций лежат
// в репозитории и в реестре на сервере, по хешу сервер находит текст.

import manifest from '../../legal-editions/manifest.json';
import type { LegalOperator } from './operator';

export type LegalDocumentId =
  | 'terms-of-use'
  | 'privacy-policy'
  | 'ai-data-consent'
  | 'ai-notice'
  | 'cookie-notice'
  | 'marketing-consent'
  // CORE-1928 — полный текст согласия на рекламу; `marketing-consent` — строка галочки.
  | 'marketing-consent-full'
  | 'personal-data-consent'
  | 'live-recording-notice';

/**
 * Язык текста или редакция по юрисдикции (у соглашения и политики `en` — редакция для нерезидентов РФ).
 * `-intl` — тот же язык, оператор AGAPE.IO Limited (согласие на ИИ, CORE-1661).
 */
export type LegalVariant = 'ru' | 'en' | 'es' | 'ru-intl' | 'en-intl';

/** То, что уходит на сервер вместе с согласием: документ, версия, вариант и SHA-256 его текста. */
export interface AcceptedDocument {
  documentId: LegalDocumentId;
  version: string;
  variant: LegalVariant;
  sha256: string;
}

interface ManifestEdition extends AcceptedDocument {
  current?: boolean;
}

const CURRENT = (manifest.editions as ManifestEdition[]).filter((e) => e.current);

/** Вариант по языку интерфейса: ru / en / es, всё прочее — ru (как и сами тексты). */
export function languageVariant(language: string | null | undefined): LegalVariant {
  const lang = (language || 'ru').toLowerCase();
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('es')) return 'es';
  return 'ru';
}

/**
 * Вариант текста согласия на ИИ (CORE-1661): язык экрана (у него свой
 * переключатель RU/EN) и оператор, которого экран называет.
 */
export function aiConsentVariant(lang: string | null | undefined, operator: LegalOperator): LegalVariant {
  const base = languageVariant(lang) === 'en' ? 'en' : 'ru';
  return operator === 'intl' ? `${base}-intl` : base;
}

/**
 * Действующая редакция документа в нужном варианте. Если такого варианта у
 * документа нет (испанского текста согласия на ИИ, например), берётся русский —
 * его человек и видит.
 */
export function currentEdition(documentId: LegalDocumentId, variant: LegalVariant): AcceptedDocument {
  const found =
    CURRENT.find((e) => e.documentId === documentId && e.variant === variant)
    ?? CURRENT.find((e) => e.documentId === documentId && e.variant === 'ru');
  if (!found) throw new Error(`legal edition is missing: ${documentId}`);
  return { documentId: found.documentId, version: found.version, variant: found.variant, sha256: found.sha256 };
}

/**
 * Документы, которые человек принимает галочкой на регистрации и на экране
 * согласия после соцвхода: соглашение и политика в редакции его юрисдикции
 * (`utils/legalJurisdiction`).
 *
 * CORE-1932 — в российском контуре галочка ещё и даёт согласие на обработку
 * ПДн (`personal-data-consent`, только русский текст АО). Вне российского
 * контура его нет: там английские Условия AGAPE.IO Limited, а согласия от этого
 * юрлица пока нет (CORE-1559) — галочка и запись прежние.
 */
export function registrationDocuments(foreignJurisdiction: boolean): AcceptedDocument[] {
  if (foreignJurisdiction) {
    return [currentEdition('terms-of-use', 'en'), currentEdition('privacy-policy', 'en')];
  }
  return [
    currentEdition('terms-of-use', 'ru'),
    currentEdition('personal-data-consent', 'ru'),
    currentEdition('privacy-policy', 'ru'),
  ];
}
